import { useEffect } from "react";

/**
 * Reference-counted body scroll lock shared by every overlay that needs one -
 * the mobile nav drawer, the admins create-user dialog, and any future modal.
 *
 * A naive "save `overflow`, set it to `hidden`, restore it on close" effect
 * works in isolation but breaks the moment two overlays can be open at once:
 * each one captures whatever `overflow` happens to be when *it* mounts, so
 * whichever one closes second stamps its own (possibly stale) saved value back
 * over the one the other overlay still needs. Counting locks means the value
 * is only captured on the 0->1 transition and only restored on the 1->0
 * transition, so nested or overlapping locks compose instead of clobbering
 * each other.
 *
 * The scroll position is captured alongside `overflow` for the same reason
 * `overflow: hidden` needs it: locking scroll does not itself move the page,
 * but focus moving into a `position: fixed` overlay can scroll a stale
 * position back into view once the lock lifts if it was never pinned down.
 */
let lockCount = 0;
let previousOverflow = "";
let previousScrollY = 0;

function acquireLock() {
  if (lockCount === 0) {
    previousOverflow = document.body.style.overflow;
    previousScrollY = window.scrollY;
    document.body.style.overflow = "hidden";
  }
  lockCount += 1;
}

function releaseLock() {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount === 0) {
    document.body.style.overflow = previousOverflow;
    // Only actually scroll if the position drifted: focus moving into the
    // overlay is the only thing that can do that (see the comment above), so
    // in the common case - closed the same way it was opened - this is a no-op
    // and skips calling into an API real browsers rarely need asked twice.
    if (window.scrollY !== previousScrollY) {
      window.scrollTo(0, previousScrollY);
    }
  }
}

/** Locks body scroll for as long as `active` is true. */
export function useBodyScrollLock(active: boolean) {
  useEffect(() => {
    if (!active) return;

    acquireLock();
    return releaseLock;
  }, [active]);
}
